import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, MapPin, X } from "lucide-react";
import api from "../lib/api";

const inputCls =
  "w-full rounded-2xl border border-line bg-cream px-5 py-4 text-base text-ink placeholder:text-inkSoft/50 transition-[border-color] duration-200 focus:border-marmalade focus:outline-none";

const AdminServiceArea = () => {
  const [area, setArea] = useState(null);
  const [baseQuery, setBaseQuery] = useState("");
  const [radius, setRadius] = useState(15);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testZip, setTestZip] = useState("");
  const [testResult, setTestResult] = useState("");
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    api
      .get("/service-area")
      .then((r) => {
        setArea(r.data);
        setEnabled(r.data.enabled);
        setRadius(r.data.radius_miles || 15);
        setBaseQuery(r.data.base_zip || "");
      })
      .catch(() => setLoadError(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/service-area", {
        base_query: baseQuery,
        radius_miles: Number(radius),
        enabled,
      });
      setArea(data);
      setBaseQuery(data.base_zip);
      toast.success(
        data.enabled
          ? `Service area set — ${data.base_label} + ${data.radius_miles} miles.`
          : "Service area filter turned off — all bookings accepted."
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save — please try again.");
    } finally {
      setSaving(false);
    }
  };

  const check = async () => {
    if (!/^\d{5}$/.test(testZip)) {
      setTestResult("Enter a 5-digit zip code to check.");
      return;
    }
    try {
      const { data } = await api.get(`/service-area/check?zip=${testZip}`);
      setTestResult(data.enabled ? data.message : "The filter is off — all bookings are accepted right now.");
    } catch {
      setTestResult("Could not check that zip — please try again.");
    }
  };

  if (!area) {
    return loadError ? (
      <p className="mt-8 text-inkSoft">Could not load your service area — refresh the page to try again.</p>
    ) : (
      <p className="mt-8 text-inkSoft">Loading your service area…</p>
    );
  }

  return (
    <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12">
      <form
        onSubmit={save}
        className="h-fit rounded-3xl border border-line bg-white p-8 lg:col-span-5"
        data-testid="service-area-form"
      >
        <h3 className="font-display text-2xl font-medium text-ink">Where do you travel?</h3>
        <p className="mt-2 text-sm leading-relaxed text-inkSoft">
          Bookings outside this radius are politely declined at the source — customers see it as they type
          their zip.
        </p>

        <label className="mt-6 flex items-center justify-between rounded-2xl bg-sand p-5">
          <span className="text-sm font-semibold text-ink">Only accept bookings in my service area</span>
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            data-testid="service-area-toggle"
            aria-pressed={enabled}
            className={`relative h-8 w-14 shrink-0 rounded-full transition-colors duration-300 ${enabled ? "bg-forest" : "bg-line"}`}
          >
            <span
              className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition-[left] duration-300 ${enabled ? "left-7" : "left-1"}`}
            />
          </button>
        </label>

        <div className="mt-6">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">Your home zip code (center of the map)</span>
          <input
            value={baseQuery}
            onChange={(e) => setBaseQuery(e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="e.g. 11201"
            data-testid="service-area-zip"
            className={`${inputCls} mt-2`}
          />
        </div>

        <div className="mt-4">
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-inkSoft">How far will you travel? (miles)</span>
          <input
            type="number"
            min={1}
            max={100}
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            data-testid="service-area-radius"
            className={`${inputCls} mt-2`}
          />
        </div>

        {area.enabled && area.base_label && (
          <p className="mt-4 flex items-center gap-2 rounded-2xl bg-forest/10 p-4 text-sm text-forest" data-testid="service-area-current">
            <MapPin className="h-4 w-4 shrink-0" />
            Currently serving {area.base_label} + {area.radius_miles} miles
          </p>
        )}

        <button
          type="submit"
          disabled={saving}
          data-testid="service-area-save"
          className="mt-6 w-full rounded-full bg-forest py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-[background-color] duration-300 hover:bg-ink disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save service area"}
        </button>
      </form>

      <div className="h-fit rounded-3xl border border-line bg-white p-8 lg:col-span-7" data-testid="service-area-checker">
        <h3 className="font-display text-2xl font-medium text-ink">Try a zip code</h3>
        <p className="mt-2 text-sm leading-relaxed text-inkSoft">
          Check how any zip measures up against your radius — this is exactly what customers see.
        </p>
        <div className="mt-6 flex flex-col gap-4 sm:flex-row">
          <input
            value={testZip}
            onChange={(e) => setTestZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            placeholder="Customer zip, e.g. 10001"
            data-testid="service-area-test-zip"
            className={`${inputCls} flex-1`}
          />
          <button
            type="button"
            onClick={check}
            data-testid="service-area-check-btn"
            className="rounded-full bg-marmalade px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-[background-color] duration-300 hover:bg-marmalade-deep"
          >
            Check
          </button>
        </div>
        {testResult && (
          <div
            className={`mt-5 flex items-start gap-3 rounded-2xl p-5 text-sm leading-relaxed ${
              testResult.includes("Great news") || testResult.includes("off") ? "bg-forest/10 text-forest" : "bg-red-50 text-red-700"
            }`}
            data-testid="service-area-test-result"
          >
            {testResult.includes("Great news") || testResult.includes("off") ? (
              <Check className="mt-0.5 h-4 w-4 shrink-0" />
            ) : (
              <X className="mt-0.5 h-4 w-4 shrink-0" />
            )}
            {testResult}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminServiceArea;
